import { pipe, Option, Record } from "fp";

import { Cache, ID } from "~/Data";
import { Remote } from "~/Remote";
import { Time } from "~/Time";

export type Identity = {
  readonly interval: Time.Interval;
};

export type Stream<A> = Identity & {
  readonly providers: Cache.Data<DataProvider<A>>;
};

export type StreamedData<A> = {
  readonly id: ID.ID;
  readonly data: A;
};

export const empty = <A>(
  interval: Time.Interval = Time.Interval.fromDateTimes(Time.DateTime.now())(
    Time.DateTime.now()
  )
): Stream<A> => ({
  interval,
  providers: {},
});

export type DataProvider<A> = () => Remote<A>;

export type Streamable<A> = {
  readonly id: ID.ID;
  readonly dataFn: DataProvider<A>;
};

export const fromList = <A, B>(
  mapWithID: (a: Streamable<A>) => readonly [ID.ID, DataProvider<B>]
) => (list: readonly Streamable<A>[], interval?: Time.Interval): Stream<B> => {
  const stream: Stream<B> = empty<B>(interval);

  // eslint-disable-next-line functional/no-loop-statement
  for (const a of list) {
    const [id, b] = mapWithID(a);
    // eslint-disable-next-line functional/no-expression-statement,functional/immutable-data
    stream.providers[id] = b;
  }

  return stream;
};

export const filterStream = <A>(
  filter?: Cache.Filter.Filter<DataProvider<A>, DataProvider<A>>
) => (stream: Stream<A>): readonly StreamedData<A>[] => {
  return pipe(
    stream.providers,
    Record.filterMapWithIndex((id, a) =>
      !filter
        ? Option.some(a)
        : pipe(filter(id, a), (filtered) =>
            filtered ? Option.some(filtered) : Option.none
          )
    ),
    Record.filterMapWithIndex<ID.ID, DataProvider<A>, A>((_id, a) =>
      pipe(Remote.toUndefined<A>(a()), (filtered) =>
        filtered ? Option.some(filtered) : Option.none
      )
    ),
    Record.collect((id, a) => ({ id, data: a }))
  );
};

export const toList = <A, B>(
  map: (id: ID.ID, a: DataProvider<A>) => Remote<B>
) => ({ providers }: Stream<A>): readonly Remote<B>[] =>
  pipe(providers, Record.collect(map));
